export interface MarginValues {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface MarginsZoneProps {
  value: MarginValues;
  onChange: (next: MarginValues) => void;
  showFirstPageMargins: boolean;
  onChangeShowFirstPageMargins: (next: boolean) => void;
}

interface Preset {
  label: string;
  values: MarginValues;
}

const PRESETS: Preset[] = [
  { label: "Normal (0.75 / 0.50)", values: { top: 0.75, bottom: 0.75, left: 0.5, right: 0.5 } },
  { label: "Narrow (0.50 / 0.25)", values: { top: 0.5, bottom: 0.5, left: 0.25, right: 0.25 } },
  { label: "Wide (1.0 / 0.75)", values: { top: 1.0, bottom: 1.0, left: 0.75, right: 0.75 } },
];

function presetMatch(value: MarginValues): string {
  for (const p of PRESETS) {
    const v = p.values;
    if (
      v.top === value.top &&
      v.bottom === value.bottom &&
      v.left === value.left &&
      v.right === value.right
    ) {
      return p.label;
    }
  }
  return "";
}

function clamp(n: number): number {
  if (Number.isNaN(n)) return 0.25;
  return Math.min(3.0, Math.max(0.25, n));
}

export function MarginsZone({
  value,
  onChange,
  showFirstPageMargins,
  onChangeShowFirstPageMargins,
}: MarginsZoneProps) {
  const matchedPreset = presetMatch(value);

  function setSide(side: keyof MarginValues, raw: string) {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) {
      onChange({ ...value, [side]: 0 });
      return;
    }
    onChange({ ...value, [side]: clamp(parsed) });
  }

  function applyPreset(label: string) {
    const preset = PRESETS.find((p) => p.label === label);
    if (preset) onChange(preset.values);
  }

  return (
    <div className="space-y-3">
      <select
        value={matchedPreset}
        onChange={(e) => {
          const label = e.target.value;
          if (label) applyPreset(label);
        }}
        className="w-full text-[12.5px] hair border rounded-md px-2 py-1.5 bg-canvas/30 focus:outline-none focus:ring-2 focus:ring-tide/40"
      >
        <option value="">Custom</option>
        {PRESETS.map((p) => (
          <option key={p.label} value={p.label}>
            {p.label}
          </option>
        ))}
      </select>

      <div className="bg-canvas/40 rounded-md p-4 flex items-center gap-4">
        <div
          className="relative bg-paper-hi hair border rounded-sm flex-shrink-0"
          style={{ width: 112, height: 128 }}
          aria-hidden="true"
        >
          <div className="absolute inset-2 hair border border-dashed border-tide/60 rounded-sm flex items-center justify-center">
            <span className="text-[9px] text-tide font-medium">Body</span>
          </div>
          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] text-ink-4 num">
            {value.top.toFixed(2)}
          </span>
          <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 text-[9px] text-ink-4 num">
            {value.bottom.toFixed(2)}
          </span>
          <span className="absolute top-1/2 -translate-y-1/2 -left-4 text-[9px] text-ink-4 num">
            {value.left.toFixed(2)}
          </span>
          <span className="absolute top-1/2 -translate-y-1/2 -right-4 text-[9px] text-ink-4 num">
            {value.right.toFixed(2)}
          </span>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          {(["top", "bottom", "left", "right"] as const).map((side) => (
            <label key={side}>
              <span className="text-[10px] text-ink-4 uppercase tracking-wider">
                {side[0].toUpperCase() + side.slice(1)}
              </span>
              <input
                type="number"
                step="0.05"
                min="0.25"
                max="3.0"
                value={value[side]}
                onChange={(e) => setSide(side, e.target.value)}
                className="w-full mt-0.5 px-2 py-1 rounded hair border bg-paper-hi text-[12px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none mt-1">
        <span
          className={`relative inline-block w-9 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
            showFirstPageMargins ? "bg-tide" : "bg-ink-5"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-paper-hi transition-transform shadow-sm ${
              showFirstPageMargins ? "translate-x-4" : ""
            }`}
          />
          <input
            type="checkbox"
            className="sr-only"
            checked={showFirstPageMargins}
            onChange={(e) => onChangeShowFirstPageMargins(e.target.checked)}
            aria-label="Show margin content on first page"
          />
        </span>
        <span className="text-[12.5px]">
          <span className="font-medium">Show margin content on first page</span>
          <span className="text-ink-4 block text-[11.5px] mt-0.5">
            When on, the top-left, top-right, and bottom-left margin
            content (including page numbers) appears on page 1. When
            off, page 1 shows only the header and booking ID.
          </span>
        </span>
      </label>
    </div>
  );
}
